<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="6">
        <v-card class="h-100">
          <v-card-text>
            <VehicleMetrics
              v-model:data="data"
              v-model:dataToCompare="dataToCompare"
            />
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="h-100">
          <v-card-text>
            <VehicleCtr
              v-model:data="data"
              v-model:dataToCompare="dataToCompare"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import {
  formatDate,
  getCurrentMonthBounds,
  getLastMonthBounds,
} from '@/stores/utils'
import type {
  IVehicleAnalytics,
  IVehicleAnalyticsAggregated,
} from '@/components/dashboard/types'
import VehicleMetrics from '@/components/dashboard/components/VehicleMetrics.vue'
import VehicleCtr from '@/components/dashboard/components/VehicleCtr.vue'
import { getMustComparedAnalytics } from '@/components/dashboard/utils'

const props = defineProps<{
  accountId: string
  accountName: string
  placements: string[]
  startDate?: Date | null
  endDate?: Date | null
  previousStartDate?: Date | null
  previousEndDate?: Date | null
}>()

const data = ref<IVehicleAnalyticsAggregated | null>(null)
const dataToCompare = ref<IVehicleAnalyticsAggregated | null>(null)

const getChartData = async (
  accountId: string,
  accountName: string,
  placements: string[],
  startDate: string,
  endDate: string,
  comparedStartDate: string,
  comparedEndDate: string,
): Promise<{
  current: IVehicleAnalyticsAggregated
  previous: IVehicleAnalyticsAggregated
}> => {
  // If no placements are selected, return empty data to avoid API errors
  if (!placements || placements.length === 0) {
    const emptyData: IVehicleAnalyticsAggregated = {
      count: 0,
      clicks: 0,
      views: 0,
      ctr: 0,
    }
    return {
      current: emptyData,
      previous: emptyData,
    }
  }

  const productId = 'micro_srp'
  const vehicleViewsAnalytics = await getMustComparedAnalytics(
    accountId,
    accountName,
    productId,
    ['stockNumber'],
    ['_events', 'eventType', 'source', 'account', 'placement'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'vehicle_view',
      },
    ],
    startDate,
    endDate,
    comparedStartDate,
    comparedEndDate,
    placements,
  )
  const vehicleClicksAnalytics = await getMustComparedAnalytics(
    accountId,
    accountName,
    productId,
    ['stockNumber'],
    ['_events', 'eventType', 'source', 'account', 'placement'],
    [
      {
        name: 'eventType',
        operator: '=',
        value: 'vehicle_click',
      },
    ],
    startDate,
    endDate,
    comparedStartDate,
    comparedEndDate,
    placements,
  )

  // Derive counts and totals from the union of vehicles with views or clicks
  const currentViewItems = vehicleViewsAnalytics.current?.items ?? []
  const currentClickItems = vehicleClicksAnalytics.current?.items ?? []
  const currentVehicleSet = new Set<string>(
    currentViewItems
      .map(i => i.dimensions?.stockNumber)
      .filter(Boolean) as string[],
  )
  currentClickItems.forEach(i => {
    const sn = i.dimensions?.stockNumber as string | undefined
    if (sn) currentVehicleSet.add(sn)
  })
  const currentCount = currentVehicleSet.size
  const currentViews = currentViewItems.reduce(
    (sum, item) => sum + (item.metrics?.['_events'] ?? 0),
    0,
  )
  const currentClicks = currentClickItems.reduce(
    (sum, item) => sum + (item.metrics?.['_events'] ?? 0),
    0,
  )
  const currentData: IVehicleAnalyticsAggregated = {
    count: currentCount,
    clicks: currentClicks,
    views: currentViews,
    ctr: currentViews === 0 ? 0 : (currentClicks / currentViews) * 100,
  }

  const previousViewItems = vehicleViewsAnalytics.previous?.items ?? []
  const previousClickItems = vehicleClicksAnalytics.previous?.items ?? []
  const previousVehicleSet = new Set<string>(
    previousViewItems
      .map(i => i.dimensions?.stockNumber)
      .filter(Boolean) as string[],
  )
  previousClickItems.forEach(i => {
    const sn = i.dimensions?.stockNumber as string | undefined
    if (sn) previousVehicleSet.add(sn)
  })
  const previousCount = previousVehicleSet.size
  const previousViews = previousViewItems.reduce(
    (sum, item) => sum + (item.metrics?.['_events'] ?? 0),
    0,
  )
  const previousClicks = previousClickItems.reduce(
    (sum, item) => sum + (item.metrics?.['_events'] ?? 0),
    0,
  )
  const previousData: IVehicleAnalyticsAggregated = {
    count: previousCount,
    clicks: previousClicks,
    views: previousViews,
    ctr: previousViews === 0 ? 0 : (previousClicks / previousViews) * 100,
  }

  return {
    current: currentData,
    previous: previousData,
  }
}

onMounted(async () => {
  await handleDateChange()
})

const handleDateChange = async () => {
  const currentBounds = getCurrentMonthBounds()
  const lastBounds = getLastMonthBounds()

  const chartData = await getChartData(
    props.accountId,
    props.accountName,
    props.placements,
    formatDate(currentBounds.start),
    formatDate(currentBounds.end),
    formatDate(lastBounds.start),
    formatDate(lastBounds.end),
  )

  data.value = chartData.current
  dataToCompare.value = chartData.previous
}

watch(
  () => [props.placements],
  async () => {
    await handleDateChange()
  },
)
</script>

<style scoped>
/* Mobile-first responsive styling */
.vehicles-chart {
  width: 100%;
  height: 100%;
}

/* Ensure charts are properly sized on mobile */
@media (max-width: 959px) {
  .vehicles-chart {
    min-height: 300px;
  }

  /* Reduce chart height on very small screens */
  @media (max-width: 599px) {
    .vehicles-chart {
      min-height: 250px;
    }
  }
}

/* Ensure proper spacing between stacked cards on mobile */
@media (max-width: 959px) {
  .v-col:not(:last-child) {
    margin-bottom: 16px;
  }
}
</style>
